import * as React from "react";
import { MoreHorizontal } from "lucide-react";
import { BackgroundTileCrop } from "@/components/Backgrounds/BackgroundTileCrop";
import { Badge, Button, DropdownMenu, Spinner } from "@/components/ui";
import { cn } from "@/lib/utils";
import { BackgroundRatio } from "@/services/bedrock/dtos/BackgroundAsset";
import { DesignBackground } from "@/services/bedrock/dtos/DesignPackage";

export type BackgroundTileState = "default" | "idle" | "trying" | "setting" | "removing";

interface BackgroundTileProps {
  background: DesignBackground;
  /** The slide's slot ratio; the thumbnail is cropped the way the template shows it. */
  ratio: BackgroundRatio;
  state: BackgroundTileState;
  canEdit: boolean;
  canRemove: boolean;
  onTry(): void;
  onSetDefault(): void;
  onRefine(): void;
  onRemove(): void;
}

export const BackgroundTile: React.FC<BackgroundTileProps> = ({
  background,
  ratio,
  state,
  canEdit,
  canRemove,
  onTry,
  onSetDefault,
  onRefine,
  onRemove,
}) => {
  const isDefault = state === "default";
  const working = state === "setting" || state === "removing";

  return (
    <div
      className="group relative flex w-16 shrink-0 snap-start flex-col items-center gap-1"
      data-testid="background-tile"
    >
      <button
        type="button"
        onClick={onTry}
        disabled={working}
        aria-label={isDefault ? "Default background" : "Try this background"}
        className={cn(
          "relative w-full rounded-lg cursor-pointer",
          isDefault && "ring-2 ring-primary ring-offset-1",
          working && "opacity-60 cursor-default"
        )}
      >
        <BackgroundTileCrop
          asset={{
            id: background.assetId,
            url: background.url,
            focal: background.focal,
            source: background.source,
            ratios: background.ratios,
            compatible: background.compatible,
          }}
          ratio={ratio}
        />
        {(state === "trying" || working) && (
          <span className="absolute inset-0 flex items-center justify-center rounded-lg bg-white/50">
            <Spinner className="size-4 text-slate-600" />
          </span>
        )}
      </button>

      {isDefault && (
        <Badge variant="secondary" size="small" className="absolute -bottom-1 left-1/2 -translate-x-1/2">
          Default
        </Badge>
      )}
      {state === "trying" && <span className="text-[11px] text-slate-500">Trying…</span>}

      {canEdit && (
        <div className="absolute top-0.5 right-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
          <DropdownMenu>
            <DropdownMenu.Trigger
              render={
                <Button
                  variant="secondary"
                  size="icon-xs"
                  aria-label="Background options"
                  disabled={working}
                  className="shadow-sm"
                >
                  <MoreHorizontal aria-hidden="true" />
                </Button>
              }
            />
            <DropdownMenu.Content align="end">
              {!isDefault && <DropdownMenu.Item label="Set as default" onClick={onSetDefault} />}
              <DropdownMenu.Item label="Refine with AI…" onClick={onRefine} />
              {!isDefault && canRemove && <DropdownMenu.Item label="Remove" variant="destructive" onClick={onRemove} />}
            </DropdownMenu.Content>
          </DropdownMenu>
        </div>
      )}
    </div>
  );
};
